import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import './PasswordReset.css';

const API_URL = API_BASE_URL + '/api';

const PasswordReset = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug console logs
  console.log('PasswordReset component mounted');
  console.log('Token from URL:', token);
  console.log('API_URL:', API_URL);

  useEffect(() => {
    console.log('useEffect called with token:', token);
    
    const verifyToken = async () => {
      console.log('Verifying token:', token);
      try {
        const response = await fetch(`${API_URL}/verify-reset-token/${token}`);
        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response data:', data);

        if (response.ok) {
          setTokenValid(true);
          setUserEmail(data.email);
          setUserName(data.user_name);
        } else {
          setTokenValid(false);
          setError(data.error || 'Invalid or expired reset link');
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setTokenValid(false);
        setError('An error occurred while verifying the reset link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      console.error('No token found in URL');
      setError('No token found in URL');
      setTokenValid(false);
      setLoading(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/reset-password/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '500px',
          padding: '2.5rem',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '3rem 0'
          }}>
            <div 
              className="password-reset-spinner"
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #000',
                borderRadius: '50%',
                margin: '0 auto 1rem'
              }}
            ></div>
            <p style={{
              color: '#666',
              fontSize: '1.1rem',
              margin: 0
            }}>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '500px',
          padding: '2.5rem',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '2rem 0'
          }}>
            <h2 style={{
              color: '#c33',
              fontSize: '1.5rem',
              marginBottom: '1rem'
            }}>Invalid Reset Link</h2>
            <p style={{
              color: '#666',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>{error}</p>
            <p style={{
              color: '#666',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>The reset link may have expired or been used already.</p>
            <button 
              style={{
                background: '#000',
                color: 'white',
                border: 'none',
                padding: '0.875rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '1rem',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/login')}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '500px',
          padding: '2.5rem',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '2rem 0'
          }}>
            <h2 style={{
              color: '#28a745',
              fontSize: '1.5rem',
              marginBottom: '1rem'
            }}>Password Reset Successful!</h2>
            <p style={{
              color: '#666',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>Your password has been successfully reset.</p>
            <p style={{
              color: '#666',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>You can now login with your new password.</p>
            <div style={{
              backgroundColor: '#e8f5e8',
              border: '1px solid #c3e6c3',
              color: '#2d5a2d',
              padding: '1rem',
              borderRadius: '8px',
              margin: '1.5rem 0',
              fontSize: '0.95rem'
            }}>
              <p style={{ margin: 0 }}>Redirecting to login page in 3 seconds...</p>
            </div>
            <button 
              style={{
                background: '#000',
                color: 'white',
                border: 'none',
                padding: '0.875rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '1rem',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '500px',
        padding: '2.5rem',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            color: '#333',
            fontSize: '2rem',
            marginBottom: '0.5rem',
            fontWeight: '600'
          }}>Reset Your Password</h1>
          <p style={{
            color: '#666',
            fontSize: '1.1rem',
            margin: 0
          }}>Hello {userName}, enter your new password below.</p>
        </div>

        <form onSubmit={handleSubmit} style={{
          marginBottom: '2rem'
        }}>
          {error && <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            color: '#c33',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '0.95rem'
          }}>{error}</div>}
          
          <div style={{
            marginBottom: '1.5rem'
          }}>
            <label htmlFor="password" style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '500',
              color: '#333',
              fontSize: '1rem'
            }}>New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength="8"
              className="password-reset-input"
              style={{
                width: '100%',
                padding: '0.875rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{
            marginBottom: '1.5rem'
          }}>
            <label htmlFor="confirmPassword" style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '500',
              color: '#333',
              fontSize: '1rem'
            }}>Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength="8"
              className="password-reset-input"
              style={{
                width: '100%',
                padding: '0.875rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            className={`password-reset-button ${isSubmitting ? 'password-reset-button-disabled' : ''}`}
            style={{
              width: '100%',
              padding: '1rem',
              background: '#000',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              marginTop: '1rem',
              opacity: isSubmitting ? '0.6' : '1',
              transition: 'all 0.3s ease'
            }}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '2rem'
        }}>
          <button 
            className="password-reset-link"
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              textDecoration: 'none',
              padding: '0.5rem 0'
            }}
            onClick={() => navigate('/login')}
          >
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;

// Add a fallback component just in case
const PasswordResetFallback = () => (
  <div style={{ 
    padding: '20px', 
    backgroundColor: 'yellow', 
    color: 'black',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <div>
      <h2>Password Reset Component Error</h2>
      <p>Something went wrong with the password reset page.</p>
      <p>Current URL: {window.location.href}</p>
      <button onClick={() => window.location.href = '/login'}>
        Back to Login
      </button>
    </div>
  </div>
);

export { PasswordResetFallback }; 